import type { ScopeFlagFact } from "@/features/material-compliance/api/types"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

/** One flag, ready to render: the copy key and the values it interpolates.
 *  Never the backend's own English sentence (`ScopeFlagFact.text`). */
export interface ScopeFlagNote {
  key: TranslationKeys
  values: Record<string, string | number>
}

/** Descending weights, every subtractive pair included. The loop below is
 *  greedy, so a missing pair does not refuse its value, it spells the value
 *  out wrong (40 came back as "XXXX"). The table therefore has to cover the
 *  whole range `annexNumeral`'s own guard admits: 1..3999. */
const ROMAN: [number, string][] = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
]

/**
 * The domain writes annexes in roman numerals ("RoHS Anhang II", "Anhang
 * III/IV" — the app's own copy and the supplier documents both), while the
 * resolver hands them over folded to `annex:<n>` so that "Annex I", "Anhang 1"
 * and "appendix i" compare equal (`annex_labels`, resolve_scope_m2.py). This
 * unfolds one value back into the numeral a reader expects.
 *
 * Anything it cannot read comes back UNCHANGED rather than as a guess: a
 * lettered annex, a future shape, or a value some other producer wrote. A
 * strange label in the note is honest; a wrong numeral is not.
 */
export function annexNumeral(value: string): string {
  const match = /^annex:(\d+)$/.exec(value)
  if (!match) return value
  let n = Number(match[1])
  if (n <= 0 || n > 3999) return value
  let out = ""
  for (const [weight, numeral] of ROMAN) {
    while (n >= weight) {
      out += numeral
      n -= weight
    }
  }
  return out
}

/**
 * ONE resolver flag mapped to this app's own copy.
 *
 * Returns null for a kind this build does not know, and for a known kind
 * whose values did not arrive — in both cases the caller renders NOTHING.
 * That is deliberate: the alternative is showing `fact.text`, a developer-
 * facing English sentence, in a German interface (the mistake
 * `footer/ApproveGatePanel.tsx` documents for `MC_DECL_V2_GATE_GENERIC`).
 * The union is open because an older run's stored facts and a newer
 * backend's kinds both reach this code (`ScopeFlagFact`'s own doc).
 *
 * `framework` and `verdict` ride on some facts and are deliberately NOT
 * rendered: the note sits on the statement card of that very framework, and
 * the verdict is the control right above it, so naming either would repeat
 * what the reader is already looking at.
 */
export function scopeFlagNote(fact: ScopeFlagFact): ScopeFlagNote | null {
  switch (fact.kind) {
    // Two readings, because the one-sentence version lied on a live
    // run: it said the withheld references had been matched by
    // article number while the completion had placed none of them, with a
    // contradicting `ambiguous_group` note directly above it on the same
    // card. `attached` is what the completion actually folded in, and a
    // missing field reads as zero: never tell the reviewer something was
    // placed unless the backend says how much.
    //
    // ONE UNIT PER SENTENCE. `count` and `shown` are REFERENCE counts (the
    // copy says "Nennungen"), so the third number in that same sentence has
    // to be one too. The fact also carries `attached_rows`, the same
    // attachment counted in table ROWS, and it is deliberately not read
    // here: swapping it in made one sentence mix references and rows, which
    // reads as an arithmetic error. The neighbouring `unshown_unmatched`
    // note keeps its own row count because it is its own sentence.
    case "withheld_cap": {
      const { count, shown, attached } = fact as Extract<
        ScopeFlagFact,
        { kind: "withheld_cap" }
      >
      if (!count || !shown) return null
      if (!attached)
        return {
          key: "MC_DECL_V2_SCOPE_FLAG_WITHHELD_CAP_NONE",
          values: { n: count, shown },
        }
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_WITHHELD_CAP",
        values: { n: count, shown, attached },
      }
    }
    case "unshown_unmatched": {
      const { count } = fact as Extract<
        ScopeFlagFact,
        { kind: "unshown_unmatched" }
      >
      if (!count) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_UNSHOWN_UNMATCHED",
        values: { n: count },
      }
    }
    // TWO backend sites raise this kind and they count different things: the
    // withheld-reference pass counts REFERENCES, the claims pre-pass counts
    // CLAIMS (rows of the document's own claim list). Nothing in the fact
    // tells them apart (both carry only `count` and `framework`), so the copy
    // says "Angaben" rather than claiming either one. Splitting the kind on
    // the backend is the real fix; until then this is the honest wording.
    case "ambiguous_group": {
      const { count } = fact as Extract<
        ScopeFlagFact,
        { kind: "ambiguous_group" }
      >
      if (!count) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_AMBIGUOUS_GROUP",
        values: { n: count },
      }
    }
    case "annex_mismatch": {
      const { count, annex, annexes } = fact as Extract<
        ScopeFlagFact,
        { kind: "annex_mismatch" }
      >
      // `annexes` carries every annex the SCOPE names; `annex` is its first
      // entry, kept for a caller that wants one. The backend sorts the list
      // as text, so "annex:10" lands before "annex:3" — re-sorted here by
      // number so the note reads "Anhang III, X".
      const listed = annexes?.length ? annexes : annex ? [annex] : []
      if (!count || listed.length === 0) return null
      const numerals = [...listed]
        .sort((a, b) => {
          const na = Number(/^annex:(\d+)$/.exec(a)?.[1] ?? NaN)
          const nb = Number(/^annex:(\d+)$/.exec(b)?.[1] ?? NaN)
          if (Number.isNaN(na) || Number.isNaN(nb)) return a.localeCompare(b)
          return na - nb
        })
        .map(annexNumeral)
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_ANNEX_MISMATCH",
        values: { n: count, annexes: numerals.join(", ") },
      }
    }
    case "annex_disagreement": {
      const { count } = fact as Extract<
        ScopeFlagFact,
        { kind: "annex_disagreement" }
      >
      if (!count) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_ANNEX_DISAGREEMENT",
        values: { n: count },
      }
    }
    case "annex_unclear": {
      const { count } = fact as Extract<
        ScopeFlagFact,
        { kind: "annex_unclear" }
      >
      if (!count) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_ANNEX_UNCLEAR",
        values: { n: count },
      }
    }
    case "restricted_ref": {
      const { count } = fact as Extract<
        ScopeFlagFact,
        { kind: "restricted_ref" }
      >
      if (!count) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_RESTRICTED_REF",
        values: { n: count },
      }
    }
    case "claim_conflict": {
      const { ref, claim } = fact as Extract<
        ScopeFlagFact,
        { kind: "claim_conflict" }
      >
      if (!ref || !claim) return null
      return {
        key: "MC_DECL_V2_SCOPE_FLAG_CLAIM_CONFLICT",
        values: { ref, claim },
      }
    }
    default:
      return null
  }
}
